import { Logger } from '@/utils/logger';
import { zodResolver } from '@hookform/resolvers/zod';
import { useControlledDialogProps } from '@montugroup/circuit-shared';
import { getTsCustomerPortalClient } from '@montugroup/circuit-shared/api/customer-portal.client';
import { ErrorMessage } from '@montugroup/circuit-shared/components/common/ErrorMessage';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  toast,
  Typography,
} from '@montugroup/design-system';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

interface SharePrescriptionForm {
  email: string;
}

const logger = new Logger('SharePrescriptionModal');
const client = getTsCustomerPortalClient();
const schema = z.object({
  email: z.string().min(1, 'Email is required').email('Enter a valid email address'),
});
const successMessage = 'Successfully shared prescription';
const errorMessage = 'Failed to share prescription';

export function SharePrescriptionModal({
  open,
  onClose,
  prescriptionId,
}: {
  open: boolean;
  onClose: () => void;
  prescriptionId: number;
}) {
  const {
    register,
    handleSubmit,
    reset: formReset,
    formState: { isValid, errors, isSubmitting },
  } = useForm<SharePrescriptionForm>({
    mode: 'onChange',
    resolver: zodResolver(schema),
    defaultValues: {
      email: '',
    },
  });
  const { mutateAsync, error, isPending, contractEndpoint, reset } = client.prescription.sendCopy.useMutation();
  const controlledDialogProps = useControlledDialogProps({
    open,
    onClose: () => {
      onClose();
      formReset();
      reset();
    },
  });

  const sharePrescription: SubmitHandler<SharePrescriptionForm> = async (formData: SharePrescriptionForm) => {
    try {
      await mutateAsync({
        params: {
          prescriptionId,
        },
        body: {
          data: {
            destination: {
              type: 'email',
              value: formData.email,
            },
          },
        },
      });

      toast.success(successMessage);
      logger.info(successMessage, { prescriptionId });
      controlledDialogProps.onClose();
    } catch (e) {
      logger.error(errorMessage, e, { prescriptionId });
    }
  };

  return (
    <Dialog {...controlledDialogProps} fullWidth>
      <DialogTitle>Share prescription</DialogTitle>
      <DialogContent>
        <Stack gap={4}>
          <Typography variant="body1" fontWeight={600}>
            Enter recipient email
          </Typography>
          <TextField
            label="Email"
            fullWidth
            {...register('email')}
            error={Boolean(errors.email)}
            helperText={errors.email?.message}
          />
        </Stack>
      </DialogContent>
      <ErrorMessage error={error} contractEndpoint={contractEndpoint} />
      <DialogActions>
        <Button
          variant="text"
          disabled={!controlledDialogProps.open || isPending}
          onClick={controlledDialogProps.onClose}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disabled={!isValid || isSubmitting}
          loading={isSubmitting}
          onClick={handleSubmit(sharePrescription)}
        >
          Share prescription
        </Button>
      </DialogActions>
    </Dialog>
  );
}
